import { SelectFiles } from "@/components/SelectFiles/SelectFiles";
import { Form, FormField } from "@/components/ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui";
import { APP_LOCAL_DATE_FORMAT } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoChapter } from "@/shared/model/edo-chapter.model";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { IEdoUnit, defaultValue } from "@/shared/model/edo-unit.model";
import {
  createEntity,
  updateEntity,
} from "@/shared/reducers/entities/edo-unit.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { getFileAvatar } from "@/shared/util/file-avatar-utils";
import { Check, X } from "lucide-react";
import "moment/locale/it";
import React, { useEffect, useState } from "react";
import { TextFormat, translate } from "react-jhipster";
import { z } from "zod";

export interface EditUnitProps {
  unit?: IEdoUnit;
  open: boolean;
  handleClose: (updated: boolean) => void;
  chapter?: IEdoChapter;
}

const schema = z.object({
  title: z.string().min(1, "Required"),
});

type FormValues = z.infer<typeof schema>;

export const EditUnit = (props: EditUnitProps) => {
  const dispatch = useAppDispatch();
  const { loading, updating, updateSuccess } = useAppSelector<
    EntityState<IEdoUnit>
  >((state) => state.edoUnit);
  const [form, setForm] = useState<IEdoUnit>({ ...defaultValue, ...props.unit });
  const [tabValue, setTabValue] = useState("info");
  const [selectedFiles, setSelectedFiles] = useState<IEdoFile[]>([]);

  const { unit, open, handleClose, chapter } = props;

  const handleSelectedFiles = (fileList: IEdoFile[]) => {
    setSelectedFiles(fileList);
  };

  const handleCloseDialog = (updated: boolean) => {
    setForm({ ...defaultValue });
    setSelectedFiles([]);
    setTabValue("info");
    handleClose(updated);
  };

  useEffect(() => {
    setForm({ ...defaultValue, ...unit });
    if (unit?.files) setSelectedFiles([...unit.files]);
  }, [unit]);

  useEffect(() => {
    if (updateSuccess) {
      handleCloseDialog(true);
    }
  }, [updateSuccess]);

  const removeFile = (index: number) => {
    const next = Array.from(selectedFiles);
    next.splice(index, 1);
    setSelectedFiles(next);
  };

  const saveEntity = (data: FormValues) => {
    const entity = { ...form, title: data.title };
    if (props.unit == null || props.unit === undefined) {
      dispatch(
        createEntity({
          ...entity,
          files: selectedFiles,
          chapter,
        } as any)
      );
    } else {
      dispatch(
        updateEntity({
          ...entity,
          files: selectedFiles,
        } as any)
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCloseDialog(false)}>
      <DialogContent
        showClose={false}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:max-h-[85vh]"
      >
        <DialogHeader className="shrink-0 flex-row items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] px-6 py-4">
          <DialogTitle>
            {translate("edocendoApp.edoUnit.home.createOrEditLabel")}
          </DialogTitle>
          <div className="flex items-center gap-1">
            <IconButton
              disabled={updating}
              onClick={() => handleCloseDialog(false)}
              aria-label="Chiudi"
            >
              <X className="h-4 w-4" />
            </IconButton>
            <IconButton
              disabled={updating}
              type="submit"
              form="edit-unit-form"
              aria-label="Salva"
            >
              <Check className="h-4 w-4" />
            </IconButton>
          </div>
        </DialogHeader>
        <Tabs
          value={tabValue}
          onValueChange={setTabValue}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <TabsList className="mx-6 mt-4 shrink-0 self-start">
            <TabsTrigger value="info">Informazioni</TabsTrigger>
            <TabsTrigger value="docs">Documenti</TabsTrigger>
          </TabsList>
          <TabsContent
            value="info"
            className="mt-0 min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-5"
          >
            {loading ? (
              <div className="flex justify-center py-4">
                <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <Form<FormValues>
                id="edit-unit-form"
                key={`${form.id ?? "new"}-${form.title ?? ""}`}
                schema={schema}
                onSubmit={saveEntity}
                defaultValues={{ title: form.title ?? "" }}
                className="flex flex-col gap-4"
              >
                <FormField
                  name="title"
                  label={translate("edocendoApp.edoUnit.title")}
                  required
                />
              </Form>
            )}
          </TabsContent>
          <TabsContent
            value="docs"
            className="mt-0 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pb-6 pt-5"
          >
            <SelectFiles
              selectedFiles={selectedFiles}
              setSelectedFiles={handleSelectedFiles}
              enableUpload={false}
            />
            <div className="overflow-hidden rounded-2xl border border-[var(--color-mockup-border)]">
              <div className="border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 px-4 py-2 text-sm font-medium text-[var(--color-mockup-text-mid)] dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/50 dark:text-[var(--color-mockup-text-light)]">
                Documenti selezionati
              </div>
              {selectedFiles?.length > 0 ? (
                <List>
                  {selectedFiles.map((file: IEdoFile, index: number) => (
                    <ListItem
                      key={file.id}
                      className="flex items-center gap-3 px-4 py-2"
                    >
                      <ListItemIcon className="shrink-0">
                        {getFileAvatar(file.fileType)}
                      </ListItemIcon>
                      <ListItemText
                        primary={file.originalFileName}
                        secondary={
                          <TextFormat
                            value={file.createdAt}
                            type="date"
                            format={APP_LOCAL_DATE_FORMAT}
                          />
                        }
                        className="min-w-0 flex-1 truncate text-sm"
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={() => removeFile(index)}
                          aria-label="Rimuovi file"
                        >
                          <X className="h-4 w-4" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <p className="p-4 text-body text-[var(--color-mockup-text-mid)]">
                  Nessun file selezionato.
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
